// Loading skeleton for listing pages built on the standard table layout
// (header with total + search, title + count + tabs + toolbar, table, footer
// with pagination). Keep in sync with Components::Skeletons::TableView (Phlex).

import { Page } from '../layouts/Page';
import { SEARCH_BAR_WIDTH } from '../../constants';
import { INVENTORY_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import { ACTIONS_COLUMN_WIDTH } from '../DataTable/constants';
import { Bar, DEFAULT_LABEL } from './primitives';

// Derived from the Inventory table's own source of truth
// (DataTable/columnWidths.ts + DataTable/constants.ts) so this can't drift
// from the real table. The server-side Phlex twin
// (app/components/skeletons/table_view.rb) can't import TS, so its
// ROW_COUNT/COLUMN_WIDTHS constants must still be kept in sync by hand.
const ROW_COUNT = 5;
const COLUMN_WIDTHS = [
  INVENTORY_COLUMN_WIDTHS.name,
  INVENTORY_COLUMN_WIDTHS.location,
  INVENTORY_COLUMN_WIDTHS.retailPrice,
  INVENTORY_COLUMN_WIDTHS.costPrice,
  INVENTORY_COLUMN_WIDTHS.defaultConfiguration,
  ACTIONS_COLUMN_WIDTH,
] as const;
const CELL_CLASSES = 'font-inter px-6 py-4 group/popout';
const BORDER_CLASSES = 'border-dinnerplate-white';

function SkeletonRow() {
  return (
    <tr className={`border-b ${BORDER_CLASSES}`}>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[0] }}>
        <div className="flex items-center gap-4">
          <Bar className="size-12 rounded-input shrink-0" />
          <div className="flex flex-col gap-1.5">
            <Bar className="h-4 w-40" />
            <Bar className="h-[21px] w-[90px] rounded-[5px]" />
          </div>
        </div>
      </td>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[1] }}>
        <Bar className="h-4 w-24" />
      </td>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[2] }}>
        <Bar className="h-4 w-20" />
      </td>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[3] }}>
        <Bar className="h-4 w-20" />
      </td>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[4] }}>
        <div className="flex flex-col gap-2">
          <Bar className="h-3 w-48" />
          <Bar className="h-3 w-36" />
        </div>
      </td>
      <td className={CELL_CLASSES} style={{ width: COLUMN_WIDTHS[5] }} />
    </tr>
  );
}

export function TableViewSkeleton({ label = DEFAULT_LABEL }: { label?: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      <Page>
        <Page.Header>
          <div className="flex items-center w-full px-16 gap-4">
            <div className="shrink-0">
              <Bar className="h-[17px] w-[169px]" />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <Bar
                className="h-[45px] w-full rounded-input"
                style={{ maxWidth: SEARCH_BAR_WIDTH }}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <Bar className="h-[17px] w-[169px]" />
            </div>
          </div>
        </Page.Header>

        <Page.Body>
          <div className="flex flex-col gap-4 flex-1 min-h-0 px-16">
            <div className="flex items-center gap-3 flex-shrink-0">
              <Bar className="h-12 w-[119px]" />
              <Bar className="h-[23px] w-[21px] rounded-xs" />
            </div>

            <div className="flex items-center justify-between gap-4 flex-shrink-0">
              <div className="flex items-center">
                <Bar className="h-[45px] w-[79px] mr-2 rounded-input" />
                <Bar className="h-[45px] w-[88px] rounded-input" />
              </div>
              <div className="flex items-center gap-3">
                <Bar className="h-[45px] w-[181px] rounded-input" />
                <Bar className="h-[45px] w-[187px] rounded-input" />
              </div>
            </div>

            <div
              className={`relative flex flex-col flex-1 min-h-0 border rounded-input bg-off-white overflow-hidden ${BORDER_CLASSES}`}
            >
              <div className="flex-1 min-h-0 overflow-hidden">
                <table className="w-full border-collapse">
                  <thead className="bg-dinnerplate-white-light">
                    <tr className={`border-b ${BORDER_CLASSES}`}>
                      {COLUMN_WIDTHS.map((width, i) => (
                        <th
                          key={i}
                          className={`${CELL_CLASSES} text-left`}
                          style={{ width }}
                        >
                          {i < COLUMN_WIDTHS.length - 1 ? <Bar className="h-4 w-24" /> : null}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: ROW_COUNT }, (_, rowIndex) => (
                      <SkeletonRow key={rowIndex} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Page.Body>

        <Page.Footer>
          <div className="flex items-center justify-between h-full px-16">
            <div className="flex items-center gap-3">
              <Bar className="h-6 w-[98px]" />
              <Bar className="h-[32px] w-[62px] rounded-input" />
            </div>
            <div className="flex items-center gap-6">
              <Bar className="size-6" />
              <div className="flex items-center gap-3">
                {Array.from({ length: 4 }, (_, i) => (
                  <Bar key={i} className="size-12 rounded-input" />
                ))}
              </div>
              <Bar className="size-6" />
            </div>
          </div>
        </Page.Footer>
      </Page>
    </div>
  );
}
